import i18next from "i18next";
import {locales} from "Constants/constants";
import getPercentileColor from "../../util/getPercentileColor";
import getLatestScores from './getLatestScores';
import getPercentileRank from './getPercentileRank';
import store from '@/store';
import SRS from '@/services/StudentRecordService';
import get from 'lodash.get';
import { formatScoreByType } from "../../util/generators/generateScore";
import { getPercentileInfoUsingScaledCutlines } from "../../util/cutscores";
import { extractScoreFromAssessment } from "../../util/extractScore";
import { ASSESSMENT_TYPE_FILTERS } from '../../../../constants/constants';

/**
 * This function calculates ARM boxes for a given student, metric, benchmark, custom benchmarks, locale, and equating.
 * @param {object} student - The student object.
 * @param {object} metric - The metric object.
 * @param {object} benchmark - The benchmark object.
 * @param {object} customBenchmarks - The custom benchmarks object.
 * @param {string} locale - The locale string.
 * @param {boolean} equating - Whether to use equated scores.
 * @returns {object} An object containing ARM boxes for each screening window.
 */
export default async function getARMBoxes(student, metric, benchmark, customBenchmarks, locale, equating = true) {
  const t = i18next.getFixedT(null, 'teacher_parent_report');
  const studentId = student && student.value;
  const armBoxes = {};

  // Get state data
  const state = store.getState();
  const districtId = get(state, 'userData.district.id');
  const schoolId = get(state, 'reports.roster.ui.selections.school.id');
  const grade = student?.grade || 1; // Default to grade 1 if not available

  if (!studentId) {
    console.warn('No student ID provided for ARM boxes');
    return {};
  }

  const formatLocale = locale?.replace("-", "_") || "EN_US";

  // Fetch assessments directly for each screening window
  const [boyAssessments, moyAssessments, eoyAssessments] = await Promise.all([
    SRS.getLatestAssessments({
      studentIds: [studentId],
      locale: formatLocale,
      equatedScores: equating,
      testType: ASSESSMENT_TYPE_FILTERS.BENCHMARK,
      screeningWindow: 'BOY'
    }),
    SRS.getLatestAssessments({
      studentIds: [studentId], 
      locale: formatLocale,
      equatedScores: equating,
      testType: ASSESSMENT_TYPE_FILTERS.BENCHMARK,
      screeningWindow: 'MOY'
    }),
    SRS.getLatestAssessments({
      studentIds: [studentId],
      locale: formatLocale, 
      equatedScores: equating,
      testType: ASSESSMENT_TYPE_FILTERS.BENCHMARK,
      screeningWindow: 'EOY'
    })
  ]);

  // Group assessments by window
  const windowAssignments = {
    BOY: boyAssessments?.[0] || null,
    MOY: moyAssessments?.[0] || null,
    EOY: eoyAssessments?.[0] || null
  };

  // Process each window (BOY, MOY, EOY)
  const windows = ['BOY', 'MOY', 'EOY'];
  for (const window of windows) {
    const assessment = windowAssignments[window];
    if (!assessment) {
      armBoxes[window] = {
        armScore: null,
        score: null,
        label: metric.value === 'ORF' ? t('WCPM') : t('ARM'),
        metric: metric.value,
        percentile: null,
        color: null,
        pacing: null,
        assignment: null,
        window
      }
      continue;
    }

    // Get the period using the assignment's periodId
    const periodId = assessment.periodId || 1
    const dateStart = assessment.dateFrom;
    const dateEnd = assessment.dateTo;

    const periodCutlines = await SRS.getScaledCutlines({
      districtId,
      schoolId,
      grade,
      scale: metric.value.toLowerCase(),
      periodId
    });

    // Get latest scores for this window
    const latestScores = assessment.scores

    // Extract score using the shared utility function
    const { score: rawScore, PR: percentile } = extractScoreFromAssessment(assessment, metric.value, metric.value);
    const score = formatScoreByType(rawScore, metric.value);

    // get pacing using getPacingFromCutlines from generateSubscores.js
    const pacing = getPercentileInfoUsingScaledCutlines(percentile, periodCutlines);
    

    // Create ARM box for this window
    armBoxes[window] = {
      armScore: formatScoreByType(latestScores.armScore, 'ARM'),
      score,
      label: metric.value === 'ORF' ? t('WCPM') : t('ARM'),
      metric: metric.value,
      percentile,
      color: pacing.color,
      pacing,
      assignment: {
        ...assessment,
        dateTo: assessment.createdAt, // Use createdAt as the completion date
        dateFrom: assessment.createdAt // Use createdAt as the start date fallback
      },
      window,
      scaledCutlines: periodCutlines
    };
  }

  return armBoxes;
}